import type { AuthorizationParams, User } from '@auth0/auth0-vue'
import { createAuth0 } from '@auth0/auth0-vue'
import { defineNuxtPlugin } from 'nuxt/app'
import { useAppConfig, useRuntimeConfig, useNuxtApp } from '#imports'

interface Tenant {
  hideVersion?: boolean
  hideAdminNavLinks?: boolean
  hideRialticOnlyViews: boolean
  singleTenantWorkspaceId: string
  auth0TenantConnection?: string
}

interface Auth0ReadyOptions {
  timeout?: number
  throw?: boolean
}

interface Auth0Config {
  AUTH0_REDIRECT_PATH: string
  AUTH0_DOMAIN: string
  AUTH0_CLIENT_ID: string
  AUTH0_AUDIENCE?: string
  AUTH0_ORG_ID?: string
}

export default defineNuxtPlugin({
  name: '001-auth0',
  parallel: true,
  setup: (nuxtApp) => {
    // Skip on server-side rendering
    if (import.meta.server || !import.meta.client) return

    const url = new URL(location.href)
    const config = nuxtApp.$config.public.auth as Auth0Config

    const params = new URLSearchParams(url.search)

    const inviteId = params.get('invitation') ?? undefined
    const orgId = params.get('organization') ?? undefined

    const plugin = createAuth0(
      {
        domain: config.AUTH0_DOMAIN,
        clientId: config.AUTH0_CLIENT_ID,
        cacheLocation: 'localstorage',
        authorizationParams: {
          redirect_uri: `${url.origin}${config.AUTH0_REDIRECT_PATH}`,

          audience: config.AUTH0_AUDIENCE,
          organization: config.AUTH0_ORG_ID ?? orgId,
          invitation: inviteId,
        },
      },
      {
        errorPath: '/auth/status',
        skipRedirectCallback: window.location.pathname === '/auth/callback',
      },
    )
    nuxtApp.vueApp.use(plugin)

    const auth0 = () => plugin

    return {
      provide: {
        auth0,
        auth0Ready: async (
          options: Auth0ReadyOptions = { timeout: 2000, throw: false },
        ): Promise<User | undefined> => {
          const { isLoading, user } = auth0()
          if (user.value?.sub) {
            return user.value
          }

          await Promise.any<boolean>([
            new Promise((resolve) => {
              const interval = setInterval(() => {
                if (!isLoading.value) {
                  clearInterval(interval)
                  resolve(true)
                }
              }, 100)
            }),
            new Promise((resolve) => setTimeout(resolve, options.timeout)),
          ])

          return user.value
        },
        loginWithRedirect: (email = '') => {
          const config = useRuntimeConfig().public
          const appConfig = useAppConfig() as { tenant?: Tenant }
          const tenantConfig = appConfig?.tenant
          const tenantConnection =
            tenantConfig?.auth0TenantConnection ||
            (typeof config.TENANT_SAML_CONNECTION === 'string'
              ? config.TENANT_SAML_CONNECTION
              : '')
          const connection: string = !email.includes('@rialtic.')
            ? tenantConnection || ''
            : ''

          const nuxtApp = useNuxtApp()
          const $datadog = nuxtApp.$datadog as unknown

          if (
            $datadog &&
            typeof $datadog === 'object' &&
            'setUser' in $datadog
          ) {
            const datadog = $datadog as {
              setUser: (user: { email: string }) => void
              setUserProperty: (key: string, value: string) => void
            }
            datadog.setUser({
              email,
            })
            datadog.setUserProperty(
              'SAML_CONNECTION',
              connection || 'Auto selected',
            )
          }

          const authorizationParams: AuthorizationParams = {
            login_hint: email,
            ...(connection ? { connection } : {}),
          }

          auth0().loginWithRedirect({ authorizationParams })
        },
      },
    }
  },
})
